export const formatNumberWithSeparators = (
  value: number | undefined,
  decimals: boolean = false
): string => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "";
  }
  
  if (decimals) {
    // For decimals, format the integer part with separators and keep decimal part (max 2 digits)
    const parts = value.toString().split(".");
    const integerPart = parts[0];
    let decimalPart = parts[1];
    // Limit to 2 decimal places
    if (decimalPart !== undefined && decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    const formattedInteger = integerPart.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ".");
    return decimalPart !== undefined ? `${formattedInteger},${decimalPart}` : formattedInteger;
  }
  
  const integerValue = Math.floor(value);
  return integerValue.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export const validateAndLimitInput = (
  value: string,
  max: number,
  previousNumericString?: string,
  decimals: boolean = false
): {
  displayValue: string;
  numericValue: number | undefined;
  shouldPrevent: boolean;
} => {
  if (decimals) {
    // Handle decimal input
    // Allow digits, comma (decimal separator), and dot (thousand separator)
    // Replace comma with dot for parsing, and remove dots (thousand separators)
    const normalizedValue = value.replaceAll(".", "").replaceAll(",", ".");
    
    // Check if there's already a decimal point
    const hasMultipleDecimals = normalizedValue.split(".").length > 2;
    
    if (hasMultipleDecimals) {
      // Multiple decimal points - use previous value
      if (previousNumericString !== undefined) {
        const previousNumeric = Number.parseFloat(previousNumericString);
        if (!Number.isNaN(previousNumeric)) {
          const previousDisplay = formatNumberWithSeparators(previousNumeric, decimals);
          return {
            displayValue: previousDisplay,
            numericValue: previousNumeric,
            shouldPrevent: true,
          };
        }
      }
      return { displayValue: "", numericValue: undefined, shouldPrevent: false };
    }
    
    // Extract numeric parts (digits and single decimal point)
    const numericString = normalizedValue.replaceAll(/[^0-9.]/g, "");
    
    // Check if input ends with a decimal separator (user is typing decimal part)
    // Check the original value before normalization to catch comma or dot
    const trimmedValue = value.trim();
    const endsWithDecimalSeparator = /[.,]$/.test(trimmedValue);
    
    // Check for decimal point and limit to 2 decimal places
    const numericDecimalIndex = numericString.indexOf(".");
    if (numericDecimalIndex !== -1) {
      const integerPart = numericString.substring(0, numericDecimalIndex);
      const decimalPart = numericString.substring(numericDecimalIndex + 1);
      
      // Limit to 2 decimal places
      if (decimalPart.length > 2) {
        // Truncate to 2 decimal places
        const truncatedDecimal = decimalPart.substring(0, 2);
        const truncatedNumericString = integerPart + "." + truncatedDecimal;
        const parsed = Number.parseFloat(truncatedNumericString);
        
        // Use previous value if available to prevent the change
        if (previousNumericString !== undefined && !Number.isNaN(parsed)) {
          const previousNumeric = Number.parseFloat(previousNumericString);
          if (!Number.isNaN(previousNumeric)) {
            const previousDisplay = formatNumberWithSeparators(previousNumeric, decimals);
            return {
              displayValue: previousDisplay,
              numericValue: previousNumeric,
              shouldPrevent: true,
            };
          }
        }
        
        // Otherwise, use truncated value
        const displayValue = formatNumberWithSeparators(parsed, decimals);
        return {
          displayValue,
          numericValue: parsed,
          shouldPrevent: false,
        };
      }
    }
    
    if (numericString === "" || numericString === ".") {
      // Allow typing just a decimal point - will show as "0,"
      if (numericString === ".") {
        return { displayValue: "0,", numericValue: 0, shouldPrevent: false };
      }
      return { displayValue: "", numericValue: undefined, shouldPrevent: false };
    }
    
    // Parse as float
    const parsed = Number.parseFloat(numericString);
    
    // Check if exceeds max
    if (!Number.isNaN(parsed) && parsed > max) {
      if (previousNumericString !== undefined) {
        const previousNumeric = Number.parseFloat(previousNumericString);
        if (!Number.isNaN(previousNumeric) && previousNumeric <= max) {
          const previousDisplay = formatNumberWithSeparators(previousNumeric, decimals);
          return {
            displayValue: previousDisplay,
            numericValue: previousNumeric,
            shouldPrevent: true,
          };
        }
      }
      // Fallback: use max value
      const numericValue = max;
      const displayValue = formatNumberWithSeparators(numericValue, decimals);
      return {
        displayValue,
        numericValue,
        shouldPrevent: false,
      };
    }
    
    // Format the display value
    let displayValue = formatNumberWithSeparators(
      Number.isNaN(parsed) ? undefined : parsed,
      decimals
    );
    
    // If user is typing a decimal (ends with comma/dot), preserve the comma in display
    // This handles cases like "123," where user is about to type decimal digits
    if (endsWithDecimalSeparator) {
      // If the formatted value already has a comma (from decimal part), don't add another
      if (!displayValue.includes(",")) {
        displayValue = displayValue + ",";
      }
    }
    
    return {
      displayValue,
      numericValue: Number.isNaN(parsed) ? undefined : parsed,
      shouldPrevent: false,
    };
  }
  
  // Original integer-only logic
  const numericString = value.replaceAll(/[^0-9]/g, "");

  if (numericString === "") {
    return { displayValue: "", numericValue: undefined, shouldPrevent: false };
  }

  // Prevent input if it would exceed 7 digits (max length)
  const maxLength = max.toString().length;
  if (numericString.length > maxLength) {
    // If we have a previous value, use it to prevent the change
    if (previousNumericString !== undefined) {
      const previousNumeric = Number.parseInt(previousNumericString, 10);
      if (!Number.isNaN(previousNumeric)) {
        const previousDisplay = formatNumberWithSeparators(previousNumeric, decimals);
        return {
          displayValue: previousDisplay,
          numericValue: previousNumeric,
          shouldPrevent: true,
        };
      }
    }
    // Otherwise, truncate to max length
    const truncated = numericString.slice(0, maxLength);
    const numericValue = Number.parseInt(truncated, 10);
    const displayValue = formatNumberWithSeparators(numericValue, decimals);
    return {
      displayValue,
      numericValue: Number.isNaN(numericValue) ? undefined : numericValue,
      shouldPrevent: false,
    };
  }

  const parsed = Number.parseInt(numericString, 10);

  // If the value exceeds max, prevent the change if we have previous value
  if (!Number.isNaN(parsed) && parsed > max) {
    if (previousNumericString !== undefined) {
      const previousNumeric = Number.parseInt(previousNumericString, 10);
      if (!Number.isNaN(previousNumeric) && previousNumeric <= max) {
        const previousDisplay = formatNumberWithSeparators(previousNumeric, decimals);
        return {
          displayValue: previousDisplay,
          numericValue: previousNumeric,
          shouldPrevent: true,
        };
      }
    }
    // Fallback: use max value
    const numericValue = max;
    const displayValue = formatNumberWithSeparators(numericValue, decimals);
    return {
      displayValue,
      numericValue,
      shouldPrevent: false,
    };
  }

  const numericValue = parsed;
  const displayValue = formatNumberWithSeparators(
    Number.isNaN(numericValue) ? undefined : numericValue,
    decimals
  );

  return {
    displayValue,
    numericValue: Number.isNaN(numericValue) ? undefined : numericValue,
    shouldPrevent: false,
  };
};

export const handleNumericKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  decimals: boolean = false
): void => {
  const key = e.key;
  const input = e.target as HTMLInputElement;
  const currentValue = input.value;
  
  // Allow comma or dot (decimal separator) when decimals is enabled
  if (decimals && (key === "," || key === ".")) {
    // Prevent if there's already a comma (our decimal separator format)
    // Dots in the value are thousand separators, not decimal separators
    // So we only prevent if there's already a comma
    if (currentValue.includes(",")) {
      e.preventDefault();
      return;
    }
    // Explicitly allow comma/dot - don't prevent, just return
    // The key will be entered and handleChange will process it
    return;
  }
  
  if (
    !/[0-9]/.test(key) &&
    ![
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ].includes(key) &&
    !(e.ctrlKey || e.metaKey) &&
    !e.altKey
  ) {
    e.preventDefault();
  }
};
